import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { Check, Sun, CloudRain, Wind, Zap } from "lucide-react";
import { getServiceSchema } from "../utils/structuredData";

export const MotorisedPergolas = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Deponti Motorised Louvered Pergolas | Pinela Deluxe & Verdeca | Hudson Knox"
        description="Motorised louvered pergolas with smart automation. Deponti Pinela Deluxe & Verdeca systems. Automated aluminium pergolas for London, Kent & South East England. Weather-responsive technology."
        keywords="motorised pergolas, louvered pergolas, automated pergolas, Pinela Deluxe, Verdeca, smart pergolas, weather-responsive pergolas, electric pergolas"
        canonicalPath="/services/motorised-pergolas"
        structuredData={getServiceSchema(
          "Motorised Louvered Pergolas",
          "Installation of automated Deponti Pinela Deluxe and Verdeca louvered pergola systems",
          "https://hudsonknox.co.uk/services/motorised-pergolas"
        )}
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1760067537956-a206c6181f56)",
            filter: "brightness(0.4)"
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Deponti Motorised Pergolas</h1>
          <p className="text-xl text-neutral-200">Pinela Deluxe & Verdeca - Aluminium louvered systems with smart automation</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-light text-neutral-900 mb-6">Deponti Pinela Deluxe & Verdeca Systems</h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              We install Deponti's premium motorised aluminium pergolas - the Pinela Deluxe with electric louvered roof (0-120° rotation) 
              and the Verdeca with retractable fabric roof. Both feature remote control operation, weather sensors, and can integrate 
              with smart home systems. Complete aluminium construction provides long-lasting durability with minimal maintenance.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/omcz2dp3_image007.png"
                alt="Deponti pergola with privacy wall"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/daomcqrb_image004.png"
                alt="Deponti louvered roof system"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light mb-12 text-center">Smart Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Sun className="h-8 w-8" />,
                title: "Adjustable Louvres",
                description: "Control sunlight and ventilation with rotating roof blades"
              },
              {
                icon: <CloudRain className="h-8 w-8" />,
                title: "Weather Sensors",
                description: "Automatic protection from rain with integrated sensors"
              },
              {
                icon: <Wind className="h-8 w-8" />,
                title: "Wind Resistance",
                description: "Engineered to withstand high winds when fully closed"
              },
              {
                icon: <Zap className="h-8 w-8" />,
                title: "LED Lighting",
                description: "Integrated ambient lighting for evening entertainment"
              }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-lg mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className="text-neutral-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* System Options */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">System Customisation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Remote Control",
                description: "Wireless remote for convenient operation from anywhere in your garden"
              },
              {
                title: "Smart Home Integration",
                description: "Connect to Alexa, Google Home, or home automation systems"
              },
              {
                title: "Rain Sensors",
                description: "Automatic closure when rain is detected to protect your space"
              },
              {
                title: "Wind Sensors",
                description: "Safety feature that closes louvres in high wind conditions"
              },
              {
                title: "Integrated Heating",
                description: "Optional infrared heating for comfortable winter outdoor living"
              },
              {
                title: "Side Screens",
                description: "Add motorised zip screens for complete weather protection"
              }
            ].map((option, index) => (
              <Card key={index} className="border-neutral-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Check className="h-6 w-6 text-neutral-900 mb-3" />
                  <h3 className="text-xl font-medium text-neutral-900 mb-3">{option.title}</h3>
                  <p className="text-neutral-600">{option.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-light text-neutral-900 mb-6">Why Choose Deponti Motorised Systems?</h2>
              <div className="space-y-4">
                {[
                  "Premium Dutch-engineered aluminium systems",
                  "Durable, low-maintenance construction",
                  "Remote control and smart home integration",
                  "Automatic rain sensor protection",
                  "Powder-coated RAL colour finishes",
                  "Quiet German motors with smooth operation",
                  "Precision-engineered modular design",
                  "LED lighting and heating integration available"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-6 w-6 text-neutral-900 flex-shrink-0 mt-1" />
                    <span className="text-neutral-600 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/2wc2g5nn_image006.png"
                alt="Deponti glass roof veranda lounge"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-white mb-4">Experience Smart Outdoor Living</h2>
            <p className="text-lg text-neutral-400">Request a quote for your motorised pergola</p>
          </div>
          <QuoteForm title="Request Motorised Pergola Quote" />
        </div>
      </section>
    </div>
  );
};
